import React, { useState, useEffect, useRef } from 'react';
import DataTable from './AllureGrid.tsx'
import { RecordForm } from './RecordForm.tsx'
import { RecordData, loadLastAllureRecord, saveLastAllureRecord } from './RecordData.tsx';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { useSearchParams } from 'react-router-dom';
import HistoriqueRecords, { type RecordHistorique } from './HistoriqueRecords.tsx';
import { saveLastVma } from './useVma.ts';
import Typography from '@mui/material/Typography';
import { libelleRecord } from '../Preferences/VmaRecord.tsx';

export function Allures({ handleLogout }: { handleLogout: () => void }) {

  // Présent quand l'utilisateur a été renvoyé ici depuis l'écran
  // Construction faute de VMA déjà enregistrée (cf Construction.tsx).
  const [searchParams] = useSearchParams();
  const vmaRequise = searchParams.get('vmaRequise') === '1';

  const [allure, setAllure] = React.useState<RecordData | undefined>(
    loadLastAllureRecord() ?? {
      type: "CINQ",
      hour: 0,
      minute: 30,
      second: 0,
    }
  );

  // Le dernier record saisi (calcule via /api/allure, cf AllureController)
  // est memorise cote serveur, par utilisateur (UserPreferenceController /
  // UserPreferenceService) - on le recharge a chaque arrivee sur l'ecran
  // pour l'afficher a nouveau plutot que la valeur par defaut, y compris
  // lors d'une nouvelle connexion (le cache sessionStorage ci-dessus ne
  // survit pas a une nouvelle session de navigateur).
  // Onglet affiche sous le formulaire : allures du record, ou historique.
  // Renvoye ici faute de VMA : on ouvre directement la saisie de record.
  const [onglet, setOnglet] = React.useState<'allures' | 'historique'>(vmaRequise ? 'historique' : 'allures');

  // Incremente apres chaque ajout de record, pour recharger l'historique.
  const [rafraichissement, setRafraichissement] = React.useState(0);
  const [message, setMessage] = React.useState<{ severity: 'success' | 'info' | 'error'; texte: string } | null>(null);

  const chargerRecordCourant = React.useCallback(() => {
    const token = sessionStorage.getItem('token');
    const request = new Request('/api/preference/record', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token,
      },
    });

    fetch(request)
      .then((response) => {
        if (response.status === 200) {
          return response.json().then((record: RecordData) => {
            setAllure(record);
            saveLastAllureRecord(record);
          });
        }
        if (response.status === 404) {
          // Aucun record memorise pour cet utilisateur : on garde la
          // valeur par defaut / la derniere valeur locale.
          return;
        }
        console.warn('Impossible de récupérer le dernier record (statut ' + response.status + ')');
      })
      .catch((error) => {
        console.warn('Erreur lors de la récupération du dernier record', error);
      });
  }, []);

  React.useEffect(() => {
    chargerRecordCourant();
  }, [chargerRecordCourant]);

  // Nouveau record : ajoute a l'historique (POST /api/preference/records),
  // puis affiche ses allures. La VMA de reference est recalculee cote
  // serveur s'il est le plus recent.
  const handleSave = (data: RecordData) => {
    fetch('/api/preference/records', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + sessionStorage.getItem('token'),
      },
      body: JSON.stringify(data),
    })
      .then(async (response) => {
        if (response.status === 201) {
          const record = (await response.json()) as RecordHistorique;
          setAllure(record);
          saveLastAllureRecord(record);
          // On reste sur l'onglet Historique : le nouveau record y apparait.
          setRafraichissement((n) => n + 1);
          if (record.courant) {
            if (record.vma !== null) saveLastVma(record.vma);
            setMessage({ severity: 'success', texte: 'Record enregistré. Ta VMA est maintenant de ' + record.vma?.toLocaleString('fr-FR') + ' km/h.' });
          } else {
            setMessage({ severity: 'info', texte: 'Record enregistré dans l\'historique. Ta VMA reste basée sur ton record plus récent.' });
          }
          return;
        }
        let texte = 'statut ' + response.status;
        try {
          const erreur = await response.json();
          if (erreur?.message) texte = erreur.message;
        } catch {
          // corps non JSON : on garde le statut
        }
        setMessage({ severity: 'error', texte: 'Record non enregistré : ' + texte });
      })
      .catch((error: Error) => setMessage({ severity: 'error', texte: 'Record non enregistré : ' + error.message }));
  };

  // Clic sur un record de l'historique : affiche ses allures sans
  // l'enregistrer a nouveau.
  const handleSelection = (record: RecordHistorique) => {
    setAllure({ type: record.type, hour: record.hour, minute: record.minute, second: record.second, date: record.date });
    setOnglet('allures');
  };

  return (
  <Box component="main" sx={{ p: 12, pt: 10 }}>

      {vmaRequise && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Aucune VMA n'est encore enregistrée pour votre compte : saisissez un record dans l'onglet Historique des records pour la calculer.
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 4, mb: 2 }}>
        <Tabs value={onglet} onChange={(_e, valeur) => setOnglet(valeur)} aria-label="Allures et historique des records">
          <Tab label="Allures" value="allures" id="onglet-allures" aria-controls="panneau-allures" />
          <Tab label="Historique des records" value="historique" id="onglet-historique" aria-controls="panneau-historique" />
        </Tabs>
      </Box>

      {onglet === 'allures' && (
        <div role="tabpanel" id="panneau-allures" aria-labelledby="onglet-allures">
          {allure && (
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Allures calculées à partir du record : {libelleRecord(allure)}
              {allure.date ? ` (${allure.date.split('-').reverse().join('/')})` : ''}
            </Typography>
          )}
          <DataTable allure={allure} handleLogout={handleLogout}/>
        </div>
      )}

      {onglet === 'historique' && (
        <div role="tabpanel" id="panneau-historique" aria-labelledby="onglet-historique">
          <RecordForm
            initialData={allure}
            onSubmit={handleSave}
          />
          <Box sx={{ mt: 4 }} />
          <HistoriqueRecords
            rafraichissement={rafraichissement}
            onSelection={handleSelection}
            onModification={chargerRecordCourant}
          />
        </div>
      )}

      {message && (
        <Snackbar open autoHideDuration={6000} onClose={() => setMessage(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert severity={message.severity} onClose={() => setMessage(null)}>
            {message.texte}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
}
